
// First Created - 27 Oct 2026
// Author - Atul Pandey
// Purpose - Transform coach session evaluation matrices into
//           chronological dimension and category progress trends


/*
|--------------------------------------------------------------------------
| 01 - Reference ID Helper
|--------------------------------------------------------------------------
|
| Handles both raw ObjectIds returned as strings and
| populated MongoDB references.
|
|--------------------------------------------------------------------------
*/

export const getEvalRefId = value => {

    if (!value) return ''

    if (typeof value === 'string') {
        return value
    }

    return String(
        value._id || value.id || value
    )

}


/*
|--------------------------------------------------------------------------
| 02 - Normalize RTK Query Collections
|--------------------------------------------------------------------------
|
| Supports:
|
| 1. RTK Query entity adapter response
|    { ids: [], entities: {} }
|
| 2. A normal array
|
|--------------------------------------------------------------------------
*/

export const getEvalCollection = data => {

    if (Array.isArray(data)) {
        return data
    }

    if (data?.ids && data?.entities) {

        return data.ids
            .map(id => data.entities[id])
            .filter(Boolean)

    }

    return []

}


/*
|--------------------------------------------------------------------------
| 03 - Date Helpers
|--------------------------------------------------------------------------
*/

const toValidDate = value => {

    if (!value) return null

    const date = new Date(value)

    return Number.isNaN(date.getTime())
        ? null
        : date

}


const toISOString = value => {

    const date = toValidDate(value)

    return date
        ? date.toISOString()
        : null

}


/*
|--------------------------------------------------------------------------
| 04 - Resolve Actual Evaluation Date
|--------------------------------------------------------------------------
|
| evaluatedAt:
|   When the evaluation was actually conducted.
|
| createdAt:
|   Fallback for historical matrices without evaluatedAt.
|
| IMPORTANT:
|   Do not use reflection note date or update date to
|   determine evaluation chronology.
|
|--------------------------------------------------------------------------
*/

export const resolveEvaluationDate = matrix => {

    return toISOString(
        matrix?.evaluatedAt ||
        matrix?.createdAt
    )

}


/*
|--------------------------------------------------------------------------
| 05 - Validate Scored Dimensions
|--------------------------------------------------------------------------
|
| Missing scores and null scores are not zero.
|
|--------------------------------------------------------------------------
*/

export const isValidEvalScore = score => {

    return (
        typeof score === 'number' &&
        Number.isInteger(score) &&
        score >= 1 &&
        score <= 5
    )

}


/*
|--------------------------------------------------------------------------
| 06 - Build Evaluation Master Data Maps
|--------------------------------------------------------------------------
*/

export const buildEvaluationMasterMaps = ({
    categories = [],
    dimensions = [],
} = {}) => {

    const categoryMap = new Map()

    const dimensionMap = new Map()

    getEvalCollection(categories).forEach(category => {

        categoryMap.set(
            getEvalRefId(category),
            category
        )

    })

    getEvalCollection(dimensions).forEach(dimension => {

        dimensionMap.set(
            getEvalRefId(dimension),
            dimension
        )

    })

    return {
        categoryMap,
        dimensionMap,
    }

}


/*
|--------------------------------------------------------------------------
| 07 - Resolve Dimension And Category Metadata
|--------------------------------------------------------------------------
*/

const resolveDimensionMetadata = (
    dimensionId,
    dimensionMap,
    categoryMap
) => {

    const dimension =
        dimensionMap.get(dimensionId)

    const categoryId = dimension
        ? getEvalRefId(dimension.categoryId)
        : ''

    const category =
        categoryMap.get(categoryId)

    return {

        dimensionId,

        dimensionName:
            dimension?.dimensionName ||
            'Historical Dimension',

        dimensionDesc:
            dimension?.dimensionDesc || '',

        dimensionActive:
            dimension?.active !== false,

        categoryId,

        categoryName:
            category?.categoryName ||
            'Uncategorized',

        categoryActive:
            category?.active !== false,

    }

}


/*
|--------------------------------------------------------------------------
| 08 - Chronological Sort
|--------------------------------------------------------------------------
|
| Sort primarily by actual evaluation date.
|
| If multiple matrices have the same evaluation date,
| use their original creation date, then matrix ID,
| for deterministic ordering.
|
|--------------------------------------------------------------------------
*/

const sortEvaluationEvents = events => {

    return [...events].sort((a, b) => {

        const dateComparison =
            new Date(a.evaluatedAt).getTime() -
            new Date(b.evaluatedAt).getTime()

        if (dateComparison !== 0) {
            return dateComparison
        }

        const createdComparison =
            new Date(a.recordedAt).getTime() -
            new Date(b.recordedAt).getTime()

        if (
            Number.isFinite(createdComparison) &&
            createdComparison !== 0
        ) {
            return createdComparison
        }

        return a.matrixId.localeCompare(b.matrixId)

    })

}


/*
|--------------------------------------------------------------------------
| 09 - Build Chronological Evaluation Events
|--------------------------------------------------------------------------
|
| Each evaluation matrix becomes one event.
|
| Note chronology is intentionally ignored.
|
| The event contains scored dimension entries,
| their evaluation notes and relevant metadata.
|
|--------------------------------------------------------------------------
*/

export const buildEvaluationEvents = ({
    matrices = [],
    categories = [],
    dimensions = [],

    coachId,
    userId,
    orgId,
    trailId,
    sessionId,
    cohortId,

} = {}) => {

    const {
        categoryMap,
        dimensionMap,
    } = buildEvaluationMasterMaps({
        categories,
        dimensions,
    })

    const filters = {
        coachId,
        userId,
        orgId,
        trailId,
        sessionId,
        cohortId,
    }

    const events = getEvalCollection(matrices)
        .filter(matrix => {

            return Object.entries(filters).every(
                ([field, expectedId]) => {

                    if (!expectedId) {
                        return true
                    }

                    return (
                        getEvalRefId(matrix[field]) ===
                        getEvalRefId(expectedId)
                    )

                }
            )

        })
        .map(matrix => {

            const evaluatedAt =
                resolveEvaluationDate(matrix)

            if (!evaluatedAt) {
                return null
            }

            const dimensionEvals = (
                matrix.sessionEvals || []
            )
                .filter(
                    item => isValidEvalScore(item.score)
                )
                .map(item => {

                    const dimensionId = getEvalRefId(
                        item.dimensionId
                    )

                    const metadata =
                        resolveDimensionMetadata(
                            dimensionId,
                            dimensionMap,
                            categoryMap
                        )

                    return {

                        ...metadata,

                        score: item.score,

                        evalNote: item.evalNote || '',

                        scoreCreatedAt:
                            toISOString(item.scoreCreatedAt),

                        scoreUpdatedAt:
                            toISOString(item.scoreUpdatedAt),

                        noteCreatedAt:
                            toISOString(item.noteCreatedAt),

                        noteUpdatedAt:
                            toISOString(item.noteUpdatedAt),

                    }

                })

            return {

                matrixId:
                    getEvalRefId(matrix),

                coachId:
                    getEvalRefId(matrix.coachId),

                userId:
                    getEvalRefId(matrix.userId),

                orgId:
                    getEvalRefId(matrix.orgId),

                trailId:
                    getEvalRefId(matrix.trailId),

                sessionId:
                    getEvalRefId(matrix.sessionId),

                cohortId:
                    getEvalRefId(matrix.cohortId),

                reflectionNoteId:
                    getEvalRefId(
                        matrix.reflectionNoteId
                    ),

                reflectionNoteType:
                    matrix.reflectionNoteType,

                evaluatedAt,

                recordedAt:
                    toISOString(matrix.createdAt) ||
                    evaluatedAt,

                dateSource:
                    toValidDate(matrix.evaluatedAt)
                        ? 'evaluatedAt'
                        : 'createdAt',

                dimensionEvals,

            }

        })
        .filter(Boolean)

    return sortEvaluationEvents(events)

}


/*
|--------------------------------------------------------------------------
| 10 - Build Dimension Movement Series
|--------------------------------------------------------------------------
|
| One series per evaluation dimension.
|
| Each observation is tied to the evaluation date,
| not the reflection note date.
|
| Multiple evaluations on the same date remain
| separate observations.
|
|--------------------------------------------------------------------------
*/

export const buildDimensionTrendSeries = (
    evaluationEvents = []
) => {

    const groups = new Map()

    evaluationEvents.forEach(event => {

        event.dimensionEvals.forEach(item => {

            const key = [
                event.coachId,
                item.dimensionId
            ].join('::')

            if (!groups.has(key)) {

                groups.set(key, {

                    coachId: event.coachId,

                    dimensionId: item.dimensionId,
                    dimensionName: item.dimensionName,
                    dimensionDesc: item.dimensionDesc,

                    categoryId: item.categoryId,
                    categoryName: item.categoryName,

                    points: [],

                })

            }

            groups.get(key).points.push({

                matrixId: event.matrixId,

                reflectionNoteId:
                    event.reflectionNoteId,

                reflectionNoteType:
                    event.reflectionNoteType,

                coachId: event.coachId,

                sessionId: event.sessionId,

                cohortId: event.cohortId,

                evaluatedAt: event.evaluatedAt,

                recordedAt: event.recordedAt,

                dateSource: event.dateSource,

                score: item.score,

                evalNote: item.evalNote,

                scoreCreatedAt:
                    item.scoreCreatedAt,

                scoreUpdatedAt:
                    item.scoreUpdatedAt,

            })

        })

    })

    return [...groups.values()]
        .map(series => {

            const points = [...series.points]
                .sort(
                    (a, b) =>
                        new Date(a.evaluatedAt) -
                        new Date(b.evaluatedAt) ||
                        a.matrixId.localeCompare(b.matrixId)
                )

            const firstPoint = points[0] || null

            const latestPoint =
                points[points.length - 1] || null

            const firstScore =
                firstPoint?.score ?? null

            const latestScore =
                latestPoint?.score ?? null

            const movement =
                firstScore !== null &&
                latestScore !== null
                    ? latestScore - firstScore
                    : null

            return {

                ...series,

                points,

                evaluationCount: points.length,

                firstScore,
                latestScore,

                movement,

                firstEvaluatedAt:
                    firstPoint?.evaluatedAt || null,

                latestEvaluatedAt:
                    latestPoint?.evaluatedAt || null,

            }

        })
        .sort(
            (a, b) =>
                a.categoryName.localeCompare(
                    b.categoryName
                ) ||
                a.dimensionName.localeCompare(
                    b.dimensionName
                ) ||
                a.coachId.localeCompare(b.coachId)
        )

}


/*
|--------------------------------------------------------------------------
| 11 - Build Category Movement Series
|--------------------------------------------------------------------------
|
| Each category observation is the average of its
| scored dimensions in one evaluation matrix.
|
| Unscored dimensions do not enter the calculation.
|
| A different count of scored dimensions may affect
| comparability; dimensionCount is retained.
|
|--------------------------------------------------------------------------
*/

export const buildCategoryTrendSeries = (
    evaluationEvents = []
) => {

    const groups = new Map()

    evaluationEvents.forEach(event => {

        const categoryEvaluations = new Map()

        event.dimensionEvals.forEach(item => {

            const categoryKey =
                item.categoryId ||
                `uncategorized:${item.dimensionId}`

            if (!categoryEvaluations.has(categoryKey)) {

                categoryEvaluations.set(categoryKey, {

                    categoryId: item.categoryId,

                    categoryName: item.categoryName,

                    values: [],

                })

            }

            categoryEvaluations
                .get(categoryKey)
                .values.push(item.score)

        })

        categoryEvaluations.forEach(categoryEval => {

            const categoryKey = [
                event.coachId,
                categoryEval.categoryId ||
                categoryEval.categoryName
            ].join('::')

            const averageScore =
                categoryEval.values.reduce(
                    (sum, score) => sum + score,
                    0
                ) / categoryEval.values.length

            if (!groups.has(categoryKey)) {

                groups.set(categoryKey, {

                    coachId: event.coachId,

                    categoryId:
                        categoryEval.categoryId,

                    categoryName:
                        categoryEval.categoryName,

                    points: [],

                })

            }

            groups.get(categoryKey).points.push({

                matrixId: event.matrixId,

                reflectionNoteId:
                    event.reflectionNoteId,

                sessionId: event.sessionId,

                cohortId: event.cohortId,

                evaluatedAt: event.evaluatedAt,

                recordedAt: event.recordedAt,

                dateSource: event.dateSource,

                score: Number(
                    averageScore.toFixed(2)
                ),

                dimensionCount:
                    categoryEval.values.length,

            })

        })

    })

    return [...groups.values()]
        .map(series => {

            const points = [...series.points]
                .sort(
                    (a, b) =>
                        new Date(a.evaluatedAt) -
                        new Date(b.evaluatedAt) ||
                        a.matrixId.localeCompare(b.matrixId)
                )

            const firstPoint = points[0] || null

            const latestPoint =
                points[points.length - 1] || null

            const firstScore =
                firstPoint?.score ?? null

            const latestScore =
                latestPoint?.score ?? null

            return {

                ...series,

                points,

                evaluationCount: points.length,

                firstScore,
                latestScore,

                movement:
                    firstScore !== null &&
                    latestScore !== null
                        ? Number(
                            (
                                latestScore -
                                firstScore
                            ).toFixed(2)
                        )
                        : null,

            }

        })
        .sort(
            (a, b) =>
                a.categoryName.localeCompare(
                    b.categoryName
                ) ||
                a.coachId.localeCompare(b.coachId)
        )

}


/*
|--------------------------------------------------------------------------
| 12 - Build Evaluation Movement Overview
|--------------------------------------------------------------------------
|
| Provides summaries for a future movement dashboard.
|
| A single scored observation has no movement yet.
|
|--------------------------------------------------------------------------
*/

export const buildEvaluationTrendOverview = ({
    evaluationEvents = [],
    dimensionSeries = [],
    categorySeries = [],
} = {}) => {

    const totalScoredObservations =
        evaluationEvents.reduce(
            (total, event) =>
                total + event.dimensionEvals.length,
            0
        )

    const dimensionSeriesWithMovement =
        dimensionSeries.filter(
            series => series.evaluationCount >= 2
        )

    const improvingDimensions =
        dimensionSeriesWithMovement.filter(
            series => series.movement > 0
        ).length

    const stableDimensions =
        dimensionSeriesWithMovement.filter(
            series => series.movement === 0
        ).length

    const decliningDimensions =
        dimensionSeriesWithMovement.filter(
            series => series.movement < 0
        ).length

    return {

        evaluationCount:
            evaluationEvents.length,

        totalScoredObservations,

        dimensionsEvaluated:
            dimensionSeries.length,

        categoriesEvaluated:
            categorySeries.length,

        dimensionsWithMovement:
            dimensionSeriesWithMovement.length,

        improvingDimensions,
        stableDimensions,
        decliningDimensions,

        firstEvaluatedAt:
            evaluationEvents[0]?.evaluatedAt || null,

        latestEvaluatedAt:
            evaluationEvents[
                evaluationEvents.length - 1
            ]?.evaluatedAt || null,

        dateFallbackCount:
            evaluationEvents.filter(
                event =>
                    event.dateSource === 'createdAt'
            ).length,

    }

}


/*
|--------------------------------------------------------------------------
| 13 - Build Complete Evaluation Trend Dataset
|--------------------------------------------------------------------------
|
| Main function for the future chart component.
|
|--------------------------------------------------------------------------
*/

export const buildCoachUserSessionEvalTrends = ({
    matrices = [],
    categories = [],
    dimensions = [],

    coachId,
    userId,
    orgId,
    trailId,
    sessionId,
    cohortId,

} = {}) => {

    const evaluationEvents = buildEvaluationEvents({

        matrices,
        categories,
        dimensions,

        coachId,
        userId,
        orgId,
        trailId,
        sessionId,
        cohortId,

    })

    const dimensionSeries =
        buildDimensionTrendSeries(
            evaluationEvents
        )

    const categorySeries =
        buildCategoryTrendSeries(
            evaluationEvents
        )

    const overview =
        buildEvaluationTrendOverview({
            evaluationEvents,
            dimensionSeries,
            categorySeries,
        })

    return {

        evaluationEvents,

        dimensionSeries,

        categorySeries,

        overview,

    }

}